import React, { useState, useMemo } from "react";
import theme from "../../../styles/theme"; // Assurez-vous que le thème est bien importé
import '../../../styles/main.css';
import DropdownMulti from "../../reusable/Forms/DropdownList";
import DropdownList from "../../reusable/Forms/DropdownList";
import TextField from "../../defined/Corelia_standard/GridText";
import TextArea from "../../reusable/Forms/TextArea";
import NumberStepField from "../../reusable/Forms/NumberStepField";
import Button from "../../reusable/Forms/Button";
import Switch from "../../reusable/Forms/Switch";
import SwitchButton from "../../reusable/Forms/SwitchButton";
import IconButton from "../../reusable/Forms/IconButton";
import RadioGroup from "../../reusable/Forms/RadioGroup";
import CheckboxGroup from "../../reusable/Forms/CheckboxGroup";

const ChevronUp = "/assets/icones/chevron-up.svg";
const ChevronDown = "/assets/icones/chevron-down.svg";

// ============================================================================
// 🔸 Composant principal
// ============================================================================

const DataGridSimple = ({
  columns = [],  // Colonnes avec la propriété `width` pour chaque colonne
  data = [],
  selectable = false,
  selectedId = null,
  boldColumns = [],
  onSelectionChange = () => {},
  editableColumns = [],  // Ajoutez cette prop pour définir les colonnes éditables
  borderEnabled = true,   // Nouvelle prop pour activer ou désactiver les bordures
}) => {
  // --------------------------------------------------------------------------
  // 🧩 États internes
  // --------------------------------------------------------------------------
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [selectedRows, setSelectedRows] = useState([]);
  const [editValue, setEditValue] = useState({}); // Pour suivre les valeurs éditées des champs TextField


  // --------------------------------------------------------------------------
  // 🧩 Sélection / Déselection des lignes
  // --------------------------------------------------------------------------
  const toggleSelection = (rowId) => {
    setSelectedRows((prevSelected) => {
      if (prevSelected.includes(rowId)) {
        return prevSelected.filter((id) => id !== rowId);
      } else {
        return [...prevSelected, rowId];
      }
    });
  };

  // --------------------------------------------------------------------------
  // Fonction pour gérer la saisie dans le TextField
  // --------------------------------------------------------------------------
  const handleTextFieldChange = (e, rowId, columnAccessor) => {
  setEditValue((prevState) => ({
    ...prevState,
    [rowId]: {
      ...prevState[rowId],
      [columnAccessor]: e.target.value, // Met à jour la valeur du champ
    },
  }));
};

  // --------------------------------------------------------------------------
  // 🔹 Tri des données
  // --------------------------------------------------------------------------
  const sortedData = useMemo(() => {
    if (!sort.key) return data;
    const dir = sort.dir === "asc" ? 1 : -1;

    return [...data].sort((a, b) => {
      const va = a[sort.key];
      const vb = b[sort.key];
      if (va == null && vb == null) return 0;
      if (va == null) return -1 * dir;
      if (vb == null) return 1 * dir;

      const na = Number(va);
      const nb = Number(vb);
      const bothNum = !Number.isNaN(na) && !Number.isNaN(nb);

      return bothNum
        ? (na - nb) * dir
        : String(va).localeCompare(String(vb), undefined, {
            sensitivity: "base",
          }) * dir;
    });
  }, [data, sort]);

  // --------------------------------------------------------------------------
  // 🔹 Rendu du tableau
  // --------------------------------------------------------------------------
  return (
    <div style={{ fontFamily: theme.DT?.font?.primary || "Arial, sans-serif" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse", 
          border: theme.DT?.borders?.width?.thin || "1px", 
          backgroundColor: theme.DT?.colors?.background?.transparent || 'rgba(0, 0, 0, 0)', 
          tableLayout: "fixed",  // Ajout de table-layout: auto ici
        }}
      >
        {/* --------------------------------------------------------------- */}
        {/* En-tête */}
        {/* --------------------------------------------------------------- */}
        <thead>
          <tr
            style={{
              backgroundColor: theme.DT?.colors?.background?.transparent || 'rgba(0, 0, 0, 0)', 
              textAlign: "left",
              borderBottom: theme.DT?.borders?.width?.thick || "2px",  // Bordure inférieure de l'en-tête toujours présente
            }}
          >
            {selectable && (
              <th
                style={{
                  width: 36,
                  padding: theme.DT?.spacing?.sm || "8px", 
                  
                  //borderBottom: `${theme.DT?.borders?.width?.thick || "2px"} solid ${theme.DT?.colors?.borders?.borderColor || "#D3D9E1"}`,
                }}
              />
            )}

            {columns.map((col) => {
              const sortable = !!col.accessor;
              const active = sort.key === col.accessor;
              const indicator = active ? (
                sort.dir === "asc" ? (
                  <img
                    src={ChevronUp}
                    alt="Chevron Up"
                    style={{
                      paddingLeft:"6px",
                      width: "18px",  
                      height: "18px", 
                      verticalAlign: "middle",  
                    }}
                  />
                ) : (
                  <img
                    src={ChevronDown}
                    alt="Chevron Down"
                    style={{
                      paddingLeft:"6px",
                      width: "18px",  
                      height: "18px", 
                      verticalAlign: "middle",  
                    }}
                  />
                )
              ) : null;

              const columnWidth = col.width || "auto";

              return (
                <th
                  key={col.accessor || col.header}
                  onClick={() => {
                    if (!sortable) return;
                    setSort((s) =>
                      s.key === col.accessor
                        ? { key: s.key, dir: s.dir === "asc" ? "desc" : "asc" }
                        : { key: col.accessor, dir: "asc" }
                    );
                  }}
                  style={{
                    padding: theme.DT?.spacing?.sm || "8px",
                    borderBottom: `1px solid ${theme.DT?.colors?.borders?.borderColor || "#A2B8C1"}`, // Bordure inférieure avec épaisseur et couleur spécifiques pour chaque cellule d'en-tête
                    borderColor: theme.DT?.colors?.borders?.borderColor || "#999999", 
                    fontWeight: theme.DT?.fontWeight?.regular || 400,
                    fontFamily: theme.DT?.font?.heading || "Roboto, sans-serif",
                    fontSize: theme.DT?.fontSize?.md || "12px",
                    color: theme.DT?.colors?.text?.primary || "#333333",
                    whiteSpace: "nowrap",
                    cursor: sortable ? "pointer" : "default",
                    userSelect: "none",
                    width: columnWidth, 
                  }}
                >
                  {col.header}
                  {indicator}
                </th>
              );
            })}
          </tr>
        </thead>

        {/* --------------------------------------------------------------- */}
        {/* Corps du tableau */}
        {/* --------------------------------------------------------------- */}
        <tbody>
          {sortedData.map((row, idx) => {
            const id = row.id ?? idx;
            const isSelected = selectedRows.includes(id);
            const isHovered = hoveredIdx === idx;

            return (
              <tr
                key={id}
                onMouseEnter={() => setHoveredIdx(idx)}
                onMouseLeave={() => setHoveredIdx(null)}
                onClick={(e) => {
                  if (e.target.type !== "checkbox") {
                    toggleSelection(id); 
                  }
                  onSelectionChange(isSelected ? null : id, isSelected ? null : row);
                }}
                style={{
                  backgroundColor: isHovered
                    ? "rgba(87, 134, 171, 0.05)" // Bleu-gris transparent premium au survol
                    : theme.DT?.colors?.background?.transparent || 'rgba(0, 0, 0, 0)', 
                  cursor: selectable ? "pointer" : "default",
                }}
              >
                {selectable && (
               <td
                  style={{
                    width: "20px",  // Fixe la largeur de la cellule contenant la case à cocher
                    padding: "0",  // Supprime le padding de la cellule
                    margin: "0",   // Supprime la marge
                    border: "none", // Supprime les bordures
                    
                    verticalAlign: "middle", // Centre la case à cocher verticalement
                    textAlign: "center", // Centre la case à cocher horizontalement
                    height: "20px",  // Hauteur de la cellule
                    overflow: "hidden",  // Cache tout débordement
                    boxSizing: "border-box",  // Assure que les bordures et padding sont inclus dans les dimensions
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleSelection(id);
                    }}
                    aria-label="Sélectionner la ligne"
                    style={{
                      width: "12px", // Taille exacte de la case à cocher
                      height: "12px", // Taille exacte de la case à cocher
                      padding: "0",  // Réduit le padding autour de la case à cocher
                      margin: "0",   // Réduit la marge autour de la case à cocher
                      display: "inline-block", // Affiche l'input en ligne
                      verticalAlign: "middle", // Aligne verticalement la case à cocher
                      border: "none", // Enlève la bordure
                    }}
                  />
                </td>
                )}
                {columns.map((col) => {
                  const columnWidth = "auto" ; 
                  const isBoldColumn = boldColumns.includes(col.header); 
                  const fontWeight = isBoldColumn ? theme.DT.fontWeight.semiBold : theme.DT.fontWeight.regular;

                 return (
                  <td
                    key={col.accessor || col.header}
                    style={{
                      margin: "0px",
                      padding: "5px",
                      borderTop: borderEnabled ? `${theme.DT?.borders?.width?.thin || "1px"} solid ${theme.DT?.colors?.borders?.borderColor || "#D3D9E1"}` : "none",  // Condition sur borderEnabled
                      borderBottom: borderEnabled ? `${theme.DT?.borders?.width?.thin || "1px"} solid ${theme.DT?.colors?.borders?.borderColor || "#D3D9E1"}` : "none",  // Condition sur borderEnabled
                      borderRight: `1px solid rgba(0, 0, 0, 0)`,
                      color: theme.DT?.colors?.text?.body || "#666666",
                      fontSize: theme.DT?.fontSize?.md || "10px",
                      width: columnWidth,
                      fontWeight: fontWeight,
                      height: "auto", // Hauteur uniforme
                      lineHeight: "normal", // Alignement de ligne uniforme pour la cellule
                    }}
                  >
                    {editableColumns.includes(col.accessor) ? (
                      <TextField
                      value={editValue[id]?.[col.accessor] || row[col.accessor]}  // Utilisation de l'état ou de la valeur d'origine
                      onChange={(e) => handleTextFieldChange(e, id, col.accessor)}  // Mise à jour de l'état lors de la saisie
                      className="custom-placeholder"
                      placeholder="Entrez une valeur"  // Ajout du placeholder
                      style={{
                        display: "flex",
                        margin: "0px",
                        padding: "0px",
                        color: theme.DT?.colors?.text?.body || "#666666",  // Couleur du texte
                        fontSize: theme.DT?.fontSize?.sm || "10px",  // Taille de la police
                        width: columnWidth,  // Largeur de la colonne
                        fontWeight: fontWeight,  // Poids de la police

                        height: "auto",  // Hauteur uniforme avec la cellule
                         
                        
                      }}
                      
                    />
                    ) : (
                      row[col.accessor]
                    )}
                  </td>
                );
                })}
              </tr>
            );
          })}

          {/* --------------------------------------------------------------- */}
          {/* Ligne vide si aucun résultat */}
          {/* --------------------------------------------------------------- */}
          {sortedData.length === 0 && (
            <tr>
              <td
                colSpan={columns.length + (selectable ? 1 : 0)}
                style={{
                  padding: "theme.DT?.spacing?.md" || "12px", 
                  textAlign: "center",
                  color: theme.DT?.colors?.text?.body || "#3F5D6E", 
                  fontStyle: "italic",
                }}
              >
                Aucun résultat
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DataGridSimple;